/**
 * A/B Test Card Component
 * Story: E007-S011 - A/B Test Manager
 * Displays comprehensive test information with variants and statistical analysis
 */

import React from 'react';
import type { ABTest } from '../types/ab-tests';
import { GOAL_METRIC_LABELS, GOAL_METRIC_FORMATS } from '../types/ab-tests';

interface ABTestCardProps {
  test: ABTest;
  onClick?: () => void;
  onDeclareWinner?: (testId: string, winnerVariantId: string) => void;
  onStop?: (testId: string) => void;
  onExport?: (testId: string) => void;
}

export const ABTestCard: React.FC<ABTestCardProps> = ({
  test,
  onClick,
  onDeclareWinner,
  onStop,
  onExport
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-warning text-white';
      case 'completed': return 'bg-success text-white';
      case 'stopped': return 'bg-bodydark text-white';
      default: return 'bg-bodydark1 text-white';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return '🟡 Running';
      case 'completed': return '🟢 Completed';
      case 'stopped': return '⚫ Stopped';
      default: return status;
    }
  };

  const formatMetric = (value: number, metric: string) => {
    const formatter = GOAL_METRIC_FORMATS[metric];
    return formatter ? formatter(value) : value.toFixed(2);
  };

  const winner = test.statistical_analysis?.winner_variant_id
    ? test.variants.find(v => v.variant_id === test.statistical_analysis.winner_variant_id)
    : null;

  return (
    <div 
      className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-6 hover:shadow-lg transition cursor-pointer"
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-black dark:text-white mb-2">
            {test.name}
          </h3>
          <div className="flex items-center gap-3 text-sm text-bodydark">
            <span>Goal: {GOAL_METRIC_LABELS[test.goal_metric]}</span>
            <span>•</span>
            <span>{test.confidence_level * 100}% Confidence</span>
            <span>•</span>
            <span>Day {test.days_running}</span>
          </div>
        </div>
        <span className={`inline-block px-3 py-1 rounded text-xs font-medium ${getStatusColor(test.status)}`}>
          {getStatusLabel(test.status)}
        </span>
      </div>

      {/* Statistical Analysis Banner */}
      {test.statistical_analysis.is_significant && winner && (
        <div className="mb-4 p-3 bg-success bg-opacity-10 border border-success rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-success text-sm">
                🏆 Winner Declared: {winner.variant_name}
              </div>
              <div className="text-xs text-bodydark mt-1">
                {test.statistical_analysis.lift_percentage?.toFixed(1)}% lift with {(test.statistical_analysis.confidence! * 100)}% confidence (p={test.statistical_analysis.p_value.toFixed(4)})
              </div>
            </div>
            {test.status === 'active' && onDeclareWinner && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeclareWinner(test.test_id, winner.variant_id);
                }}
                className="px-3 py-1 bg-success text-white rounded text-xs hover:bg-opacity-90"
              >
                Accept Winner
              </button>
            )}
          </div>
        </div>
      )}

      {test.statistical_analysis.error && (
        <div className="mb-4 p-3 bg-warning bg-opacity-10 border border-warning rounded-lg">
          <div className="text-warning text-sm">
            ⚠️ {test.statistical_analysis.error}
            {test.statistical_analysis.days_until_significant && (
              <span className="ml-2 text-bodydark text-xs">
                (~{test.statistical_analysis.days_until_significant} more days needed)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Variants Grid */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {test.variants.map((variant) => (
          <div
            key={variant.variant_id}
            className={`p-4 rounded-lg border ${
              variant.is_winner
                ? 'border-success bg-success bg-opacity-5'
                : variant.is_leader
                ? 'border-primary bg-primary bg-opacity-5'
                : 'border-stroke dark:border-strokedark'
            }`}
          >
            {/* Variant Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="font-medium text-black dark:text-white">
                {variant.variant_name}
                {variant.is_control && (
                  <span className="ml-2 text-xs text-bodydark">(Control)</span>
                )}
              </div>
              {variant.is_leader && !variant.is_winner && (
                <span className="text-xs font-medium text-primary">👑 Leading</span>
              )}
              {variant.is_winner && (
                <span className="text-xs font-medium text-success">🏆 Winner</span>
              )}
            </div>

            {/* Metric Value */}
            <div className="mb-2">
              <div className="text-2xl font-bold text-black dark:text-white">
                {formatMetric(variant.metric_value, test.goal_metric)}
              </div>
              {variant.confidence_interval[0] !== variant.confidence_interval[1] && (
                <div className="text-xs text-bodydark">
                  CI: {formatMetric(variant.confidence_interval[0], test.goal_metric)} - {formatMetric(variant.confidence_interval[1], test.goal_metric)}
                </div>
              )}
            </div>

            {/* Sample Size */}
            <div className="text-sm text-bodydark">
              {variant.impressions.toLocaleString()} impressions
            </div>
            <div className="text-sm text-bodydark">
              {variant.clicks.toLocaleString()} clicks
            </div>
            
            {/* Traffic % */}
            <div className="mt-2 pt-2 border-t border-stroke dark:border-strokedark">
              <div className="flex items-center justify-between text-xs">
                <span className="text-bodydark">Traffic</span>
                <span className="font-medium">{variant.traffic_percentage.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-stroke dark:border-strokedark">
        <div className="text-xs text-bodydark">
          Started {test.started_at ? new Date(test.started_at).toLocaleDateString() : 'N/A'}
        </div>
        <div className="flex items-center gap-2">
          {onExport && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onExport(test.test_id);
              }}
              className="px-3 py-1 border border-stroke dark:border-strokedark rounded text-xs hover:bg-gray-50 dark:hover:bg-meta-4 text-black dark:text-white"
            >
              Export
            </button>
          )}
          {test.status === 'active' && onStop && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (confirm('Stop this test early? This cannot be undone.')) {
                  onStop(test.test_id);
                }
              }}
              className="px-3 py-1 border border-danger text-danger rounded text-xs hover:bg-danger hover:text-white"
            >
              Stop Test
            </button>
          )}
          <button
            onClick={onClick}
            className="px-3 py-1 bg-primary text-white rounded text-xs hover:bg-opacity-90"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};
